import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon';
import FieldGroup from './Form/FieldGroup';
import CardTitle from './CardComponents/CardTitle';
import CardActionBox from './CardComponents/CardActionBox';
import CardMediaBox from './CardComponents/CardMediaBox';
import AttentionGrabber from './AttentionGrabber';
import Collapse from 'react-bootstrap/lib/Collapse';
import { formatClassName, pickDataAttributes } from '../Util';
import { kebabCase } from 'lodash';

const ConstructionCard = props => {
  const [expanded, setExpanded] = useState(props.expanded);
  const [showShadow, setShowShadow] = useState(props?.styleConfig?.shadow === 'light');
  const {
    alwaysCollapsed,
    alwaysExpanded,
    attentionGrabber,
    buttonConfig,
    children,
    className,
    customActionBar,
    data,
    formWrapper,
    headerImageSize,
    headerImageUrl,
    onExpandedToggle,
    size,
    styleConfig,
    title,
    titleCentered,
    titleIcon,
    titleSize
  } = props;
  const dataAttributes = pickDataAttributes(props, 'construction-card');
  if(!dataAttributes['data-action']){
    dataAttributes['data-action'] = title ? `open-${kebabCase(title)}-card` : 'open-contstruction-card';
  }
 
  const showChevron = !alwaysExpanded && !alwaysCollapsed;

  useEffect(() => {
    setExpanded(props.expanded);
  }, [props.expanded]);

  useEffect(() => {
    if (typeof onExpandedToggle === 'function') {
      onExpandedToggle(expanded);
    }
  }, [expanded]);

  useEffect(() => {
    setShowShadow(props.styleConfig?.shadow === 'light');
  }, [props.styleConfig]);

  const getCardClasses = () => formatClassName([
    'construction-card',
    `construction-card-${size}`,
    `with-title-${titleSize}`,
    `card-background-${styleConfig.background}`,
    `card-shadow-${showShadow ? 'light' : 'none'}`,
    { 'card-expanded': alwaysExpanded || expanded },
    { 'fixed-height': !!styleConfig.fixedHeight },
    { 'title-centered': titleCentered },
    { 'centered-image': !!headerImageUrl },
    className
  ]);

  const renderActions = () => {
    if (!buttonConfig && !customActionBar) {
      return null;
    }

    return customActionBar || <CardActionBox buttons={buttonConfig} className="card-actions" onClick={event => event.stopPropagation()} />;
  }

  const onMouseEnter = () => {
    if (styleConfig?.shadow === 'hover') setShowShadow(true);
  }

  const onMouseLeave = () => {
    if (styleConfig?.shadow === 'hover') setShowShadow(false);
  }

  const renderContentWrapper = children => {
    const classes = formatClassName(['construction-card-content', props.cardContentClasses]);

    return formWrapper ? (
      <FieldGroup {...props} data={data} className={classes}>
        {children}
      </FieldGroup>
    ) : (
      <div className={classes}>{children}</div>
    )
  }

  const renderCardContent = () => {
    if (alwaysCollapsed) {
      return null;
    }

    const content = renderContentWrapper((
      props.hasMedia ? <div className="media-container">{children}</div> : children
    ));

    return alwaysExpanded ? content : <Collapse in={expanded}>{content}</Collapse>;
  }

  return (
    <div
      {...dataAttributes}
      className={getCardClasses()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ maxHeight: styleConfig.fixedHeight }}
    >
      <div
        className="card-header"
        onClick={showChevron && (() => setExpanded(!expanded))}
        role={showChevron && 'button'}
      >
        {headerImageUrl &&
          <div className="media-container">
            <CardMediaBox mediaUrl={headerImageUrl} mediaSize={headerImageSize} mediaPosition="center" />
          </div>}
        <div className={formatClassName(['main', `with-chevron-${props.chevronPosition}`])}>
          {title && <CardTitle size={titleSize} icon={titleIcon}>{title}</CardTitle>}
          {showChevron && (
            <Icon
              iconType={expanded ? 'chevron-down' : 'chevron-right'}
              onClick={() => setExpanded(!expanded)}
              additionalClasses="card-chevron"
            />
          )}
          {attentionGrabber && <AttentionGrabber {...attentionGrabber} />}
        </div>
        {!titleCentered && renderActions()}

      </div>
      {renderCardContent()}
      {titleCentered && renderActions()}
    </div>
  );
};

ConstructionCard.propTypes = {
  alwaysCollapsed: PropTypes.bool,
  alwaysExpanded: PropTypes.bool,
  attentionGrabber: PropTypes.shape({
    icon: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.oneOf(['info', 'error', 'warning', 'success', 'generic'])
  }),
  buttonConfig: PropTypes.arrayOf(PropTypes.shape({
    action: PropTypes.func,
    disabled: PropTypes.bool,
    icon: PropTypes.string,
    iconFamily: PropTypes.string,
    label: PropTypes.string,
    style: PropTypes.string
  })),
  cardContentClasses: PropTypes.string,
  chevronPosition: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node,
  className: PropTypes.string,
  customActionBar: PropTypes.element,
  data: PropTypes.object,
  expanded: PropTypes.bool,
  formWrapper: PropTypes.bool,
  hasMedia: PropTypes.bool,
  headerImageSize: PropTypes.oneOf(['small', 'large', 'full']),
  headerImageUrl: PropTypes.string,
  onExpandedToggle: PropTypes.func,
  size: PropTypes.oneOf(['narrow', 'medium', 'wide', 'full']),
  styleConfig: PropTypes.shape({
    background: PropTypes.oneOf(['white', 'gray']),
    fixedHeight: PropTypes.number,
    shadow: PropTypes.oneOf(['hover', 'light', 'none'])
  }),
  title: PropTypes.string.isRequired,
  titleCentered: PropTypes.bool,
  titleIcon: PropTypes.string,
  titleSize: PropTypes.oneOf([14, 16, 18])
};

ConstructionCard.defaultProps = {
  alwaysCollapsed: false,
  alwaysExpanded: false,
  chevronPosition: 'right',
  className: '',
  expanded: false,
  formWrapper: false,
  hasMedia: false,
  headerImageSize: 'small',
  size: 'full',
  styleConfig: {
    background: 'white',
    shadow: 'none'
  },
  titleCentered: false,
  titleSize: 16
};

export default ConstructionCard;
